import React, { useState, useEffect } from 'react';
import deviceApi from '../services/device-api';
import { getSiteCurr } from '../services/managment';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  Typography,
  Box,
  Paper,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled component cho RSSI với màu sắc
const RssiIndicator = styled('span')(({ value }) => {
  let color;
  if (value >= -50) color = '#4CAF50'; // Excellent (green)
  else if (value >= -60) color = '#8BC34A'; // Good
  else if (value >= -70) color = '#FFC107'; // Fair (yellow)
  else if (value >= -80) color = '#FF9800'; // Poor
  else color = '#F44336'; // Bad (red)

  return {
    fontWeight: 'bold',
    color: color,
    display: 'inline-flex',
    alignItems: 'center',
    '&:before': {
      content: '""',
      display: 'inline-block',
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      backgroundColor: color,
      marginRight: '8px'
    }
  };
});

function GatewayListPage({ onSelect }) {
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [farmMap, setFarmMap] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [orderBy, setOrderBy] = useState('id');
  const [order, setOrder] = useState('asc');
  const [farmFilter, setFarmFilter] = useState('all');
  const [fwFilter, setFwFilter] = useState('all');

  useEffect(() => {
    deviceApi.get('/api/gateways')
      .then((res) => {
        setGateways(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    // Dùng hàm getSiteCurr để lấy dữ liệu farm
    getSiteCurr({ where: 3 })
      .then((res) => {
        if (Array.isArray(res)) {
          const map = {};
          res.forEach(item => {
            if (item.QrGateway) {
              map[item.QrGateway] = {
                farm_name: item.farm_name,
                owner_phone: item.owner_phone
              };
            }
          });
          setFarmMap(map);
        }
      })
      .catch(() => { });
  }, []);

  const handleRowClick = (gw, e) => {
    // Ngăn chặn sự kiện khi click vào nút Settings
    if (e.target.tagName !== 'BUTTON') {
      onSelect(gw);
    }
  };

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrder('asc');
    }
  };
  const farmNames = Array.from(new Set(gateways.map(gw => (farmMap[gw.id]?.farm_name || 'N/A'))));
  const firmwareList = Array.from(new Set(gateways.map(gw => gw.fw || 'N/A')));
  const sortedGateways = [...gateways].sort((a, b) => {
    let valA = a[orderBy];
    let valB = b[orderBy];
    // Xử lý null/undefined
    if (valA === undefined || valA === null) valA = '';
    if (valB === undefined || valB === null) valB = '';
    // Nếu là số thì so sánh số, nếu là chuỗi thì so sánh chuỗi
    if (!isNaN(valA) && !isNaN(valB)) {
      return order === 'asc' ? valA - valB : valB - valA;
    }
    return order === 'asc'
      ? String(valA).localeCompare(String(valB))
      : String(valB).localeCompare(String(valA));
  });
  const filteredGateways = sortedGateways.filter(gw => {
    const farmName = farmMap[gw.id]?.farm_name || 'N/A';
    const fw = gw.fw || 'N/A';
    const farmOk =
      farmFilter === 'all'
        ? true
        : farmFilter === 'N/A'
          ? farmName === 'N/A'
          : farmFilter === 'not-na'
            ? farmName !== 'N/A'
            : farmName === farmFilter;
    const fwOk =
      fwFilter === 'all'
        ? true
        : fwFilter === 'N/A'
          ? fw === 'N/A'
          : fwFilter === 'not-na'
            ? fw !== 'N/A'
            : fw === fwFilter;
    return farmOk && fwOk;
  });
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (gateways.length === 0) {
    return (
      <Box p={2} textAlign="center">
        <Typography variant="h6">No gateways found</Typography>
        <Typography variant="body2" color="textSecondary">
          No gateway devices are currently registered
        </Typography>
      </Box>
    );
  }

  return (
    
    <Box p={2} component={Paper} elevation={2}>
      <Typography variant="h6" gutterBottom>Gateway List</Typography>
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <Box>
          <Typography variant="body2" sx={{ mr: 1, display: 'inline' }}>Farm Name:</Typography>
          <select value={farmFilter} onChange={e => setFarmFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="N/A">N/A</option>
            <option value="not-na">Đang sử dụng</option>
            {farmNames.filter(f => f !== 'N/A').map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ mr: 1, display: 'inline' }}>Firmware:</Typography>
          <select value={fwFilter} onChange={e => setFwFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="N/A">Nathi</option>
            <option value="not-na">Phiên bản ĐQ</option>
            {firmwareList.filter(f => f !== 'N/A').map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </Box>
        <Typography variant="body2" sx={{ ml: 2 }}>
          Đang hiển thị: <b>{filteredGateways.length}</b> gateway
        </Typography>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size={isMobile ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'id' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => handleSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              {/* {!isMobile && (
                <TableCell sortDirection={orderBy === 'ip' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'ip'}
                    direction={orderBy === 'ip' ? order : 'asc'}
                    onClick={() => handleSort('ip')}
                  >
                    IP
                  </TableSortLabel>
                </TableCell>
              )} */}
              <TableCell sortDirection={orderBy === 'rssi' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'rssi'}
                  direction={orderBy === 'rssi' ? order : 'asc'}
                  onClick={() => handleSort('rssi')}
                >
                  Signal
                </TableSortLabel>
              </TableCell>
              {!isMobile && (
                <>
                  <TableCell sortDirection={orderBy === 'fw' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'fw'}
                      direction={orderBy === 'fw' ? order : 'asc'}
                      onClick={() => handleSort('fw')}
                    >
                      Firmware
                    </TableSortLabel>
                  </TableCell>
                  {/* <TableCell sortDirection={orderBy === 'hw' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'hw'}
                      direction={orderBy === 'hw' ? order : 'asc'}
                      onClick={() => handleSort('hw')}
                    >
                      Hardware
                    </TableSortLabel>
                  </TableCell> */}
                </>
              )}
              <TableCell sortDirection={orderBy === 'ssid' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'ssid'}
                  direction={orderBy === 'ssid' ? order : 'asc'}
                  onClick={() => handleSort('ssid')}
                >
                  Network
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'last_event' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'last_event'}
                  direction={orderBy === 'last_event' ? order : 'asc'}
                  onClick={() => handleSort('last_event')}
                >
                  Last Event
                </TableSortLabel>
              </TableCell>
              <TableCell>Farm Name</TableCell>
              <TableCell>Owner Phone</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredGateways.map((gw) => {
              const farmInfo = farmMap[gw.id] || {};
              return (
                <TableRow
                  key={gw.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={(e) => handleRowClick(gw, e)}
                >
                  <TableCell sx={{ fontWeight: '500' }}>{gw.id}</TableCell>
                  {/* {!isMobile && <TableCell>{gw.ip || 'N/A'}</TableCell>} */}

                  <TableCell>
                    <RssiIndicator value={gw.rssi}>
                      {gw.rssi} dBm
                    </RssiIndicator>
                  </TableCell>

                  {!isMobile && (
                    <>
                      <TableCell>{gw.fw || 'N/A'}</TableCell>
                      {/* <TableCell>{gw.hw || 'N/A'}</TableCell> */}
                    </>
                  )}

                  <TableCell>
                    <Box>
                      <div>{gw.ssid || 'N/A'}</div>
                      {isMobile && (
                        <Typography variant="caption" color="textSecondary">
                          {gw.ip || 'N/A'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell
                    sx={{
                      color: gw.last_event && (new Date() - new Date(gw.last_event.replace(' ', 'T') + 'Z')) > 20 * 60 * 1000
                        ? 'red'
                        : 'inherit',
                      fontWeight: gw.last_event && (new Date() - new Date(gw.last_event.replace(' ', 'T') + 'Z')) > 20 * 60 * 1000
                        ? 700
                        : 400
                    }}
                  >
                    {gw.last_event
                      ? (() => {
                        const utcDate = new Date(gw.last_event.replace(' ', 'T') + 'Z');
                        const vnDate = new Date(utcDate.getTime());
                        return vnDate.toLocaleString('en-US', { hour12: false });
                      })()
                      : 'N/A'}
                  </TableCell>
                  <TableCell>{farmInfo.farm_name || 'N/A'}</TableCell>
                  <TableCell>{farmInfo.owner_phone || 'N/A'}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onSelect(gw)}
                      sx={{ minWidth: '80px' }}
                    >
                      {isMobile ? 'Edit' : 'Settings'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

export default GatewayListPage;